import React, { useMemo, useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import styled from 'styled-components'

interface DatasetExportModalProps {
  show: boolean
  onHide: () => void
  projectName?: string
  defaultExportName?: string
  onConfirm: (exportName: string) => Promise<void> | void
}

const StyledModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
  }

  .modal-header {
    background: linear-gradient(45deg, #402563, #114ee0);
    border-bottom: none;
    padding: 1.25rem 1.5rem;

    .modal-title {
      color: white;
      font-weight: 600;
      font-size: 1.25rem;
    }
  }

  .modal-body {
    background: #2a2a2a;
    padding: 1.5rem;
  }
`

const InfoText = styled.div`
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 1rem;
`

const DatasetExportModal: React.FC<DatasetExportModalProps> = ({
  show,
  onHide,
  projectName,
  defaultExportName,
  onConfirm,
}) => {
  const computedDefaultName = useMemo(() => {
    return (
      defaultExportName ||
      `${projectName || 'dataset'}-${new Date().toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })}`
    )
  }, [defaultExportName, projectName])

  const [exportName, setExportName] = useState<string>(computedDefaultName)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    try {
      setSubmitting(true)
      setError(null)
      await onConfirm(exportName.trim() || computedDefaultName)
      onHide()
    } catch (e: any) {
      setError(e?.message || 'Failed to start export')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <StyledModal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Dataset Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InfoText>
          Export every dataset in this project, complete with COCO‑formatted labels. Name your
          export below.
        </InfoText>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3" controlId="exportName">
            <Form.Label className="text-white-50">Export name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter export name"
              value={exportName}
              onChange={(e) => setExportName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="format" hidden>
            <Form.Label className="text-white-50">Format</Form.Label>
            <Form.Select disabled defaultValue="COCO">
              <option value="COCO">COCO (coming soon)</option>
              <option value="YOLO">YOLO (coming soon)</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create Export'}
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

export default DatasetExportModal
